import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useGetIdentity } from "react-admin";
import type { paths } from "../../api/generated/api-types";
import type { Permission } from "../../auth/HasPermission";
import { logger } from "../../observability/LoggerFactory";
import { UserHierarchy } from "../../auth/UserHierarchy";
import { ROLE_LIST_WINDOW_QUERY } from "../../api/RoleListWindowQuery";

type ListRolesResponse =
  paths["/roles"]["get"]["responses"]["200"]["content"]["application/json"];
export type RoleOption = ListRolesResponse["items"][number];

// Загруженные роли держат пермишены под общим алиасом Permission (один источник для всех экранов).
type RoleChoice = Omit<RoleOption, "permissions"> & { permissions: Permission[] };

type UserResponse =
  paths["/users/{userId}"]["get"]["responses"]["200"]["content"]["application/json"];

type CreateResponse =
  paths["/users"]["post"]["responses"]["201"]["content"]["application/json"];

type Status = "active" | "inactive";
// Единая форма тела ошибки бэкенда: обработчики читают из него текст сообщения
// и перечень полей, не прошедших проверку.
interface ErrorBody {
  error: string;
  // Отсутствует, когда отказ не связан с полями формы (нет прав, конфликт
  // состояния): подсвечивать в форме тогда нечего.
  fields?: string[];
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; roles: RoleChoice[] }
  | { kind: "error"; message: string };

export interface FieldErrors {
  // Отсутствует, когда к логину претензий нет.
  login?: string;
  // Отсутствует, когда к имени претензий нет.
  name?: string;
  // Отсутствует, когда к выбранной роли претензий нет.
  roleId?: string;
  // Отсутствует, когда отказ разложен по полям: общий текст нужен только тому
  // отказу, который ни к одному полю не относится.
  general?: string;
}

export interface UseUserEditorResult {
  mode: "create" | "edit";
  // undefined в режиме создания: пользователя ещё нет, идентификатора тоже.
  userId: string | undefined;
  load: LoadState;
  login: string;
  name: string;
  roleId: string;
  status: Status;
  systemTarget: boolean;
  currentRole: { id: string; name: string; level: number } | null;
  fieldErrors: FieldErrors;
  submitting: boolean;
  createdPassword: { login: string; password: string } | null;
  setLogin: (value: string) => void;
  setName: (value: string) => void;
  setRoleId: (value: string) => void;
  setStatus: (value: Status) => void;
  submit: () => void;
  dismissCreatedPassword: () => void;
}

export function useUserEditor(onSaved: () => void): UseUserEditorResult {
  const parameters = useParams<{ id: string }>();
  const userId = parameters.id;
  const mode: "create" | "edit" = userId === undefined ? "create" : "edit";
  const { data: identity } = useGetIdentity();
  const currentLevel = identity === undefined ? Number.MAX_SAFE_INTEGER : Number(identity.level);

  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [login, setLogin] = useState("");
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState("");
  const [status, setStatus] = useState<Status>("active");
  const [systemTarget, setSystemTarget] = useState(false);
  const [currentRole, setCurrentRole] = useState<{ id: string; name: string; level: number } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [createdPassword, setCreatedPassword] = useState<{ login: string; password: string } | null>(null);

  useEffect(() => {
    let stopLoading: (() => void) | undefined = undefined;
    if (identity !== undefined) {
      let cancelled = false;

      const loadData = async () => {
        try {
          const roles = UserHierarchy.assignableRoles(await fetchRoles(), currentLevel);
          if (userId !== undefined) {
            const user = await fetchUser(userId);
            if (cancelled) {
              return;
            }
            setLogin(user.login);
            setName(user.name);
            setRoleId(user.role.id);
            setStatus(user.status === "inactive" ? "inactive" : "active");
            setSystemTarget(user.role.type === "system");
            setCurrentRole({ id: user.role.id, name: user.role.name, level: user.role.level });
          }
          if (!cancelled) {
            setLoad({ kind: "ready", roles });
          }
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : "Unknown error";
          logger.error({ err: message }, "Failed to load user editor");
          if (!cancelled) {
            setLoad({ kind: "error", message });
          }
        }
      };

      void loadData();
      stopLoading = () => {
        cancelled = true;
      };
    }
    return stopLoading;
  }, [userId, identity, currentLevel]);

  const submit = useCallback(() => {
    void (async () => {
      setSubmitting(true);
      setFieldErrors({});
      try {
        const response = await sendUser(userId, { login, name, roleId, status });
        if (!response.ok) {
          const errorBody = (await response.json()) as ErrorBody;
          setFieldErrors(toFieldErrors(response.status, errorBody));
          return;
        }
        if (userId === undefined) {
          const created = (await response.json()) as CreateResponse;
          setCreatedPassword({ login: created.login, password: created.generatedPassword });
          return;
        }
        onSaved();
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Не удалось сохранить пользователя";
        logger.error({ err: message }, "Failed to save user");
        setFieldErrors({ general: "Не удалось сохранить пользователя" });
      } finally {
        setSubmitting(false);
      }
    })();
  }, [userId, login, name, roleId, status, onSaved]);

  const dismissCreatedPassword = useCallback(() => {
    setCreatedPassword(null);
    onSaved();
  }, [onSaved]);

  return {
    mode,
    userId,
    load,
    login,
    name,
    roleId,
    status,
    systemTarget,
    currentRole,
    fieldErrors,
    submitting,
    createdPassword,
    setLogin,
    setName,
    setRoleId,
    setStatus,
    submit,
    dismissCreatedPassword,
  };
}

async function fetchRoles(): Promise<RoleOption[]> {
  const response = await fetchWithRefresh(`/api/roles?${ROLE_LIST_WINDOW_QUERY}`);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const data = (await response.json()) as ListRolesResponse;
  return data.items;
}

async function fetchUser(userId: string): Promise<UserResponse> {
  const response = await fetchWithRefresh(`/api/users/${userId}`);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return (await response.json()) as UserResponse;
}

function sendUser(
  userId: string | undefined,
  body: { login: string; name: string; roleId: string; status: Status },
): Promise<Response> {
  const isCreate = userId === undefined;
  const target = isCreate ? "/api/users" : `/api/users/${userId}`;
  const payload = isCreate
    ? { login: body.login, name: body.name, roleId: body.roleId }
    : { login: body.login, name: body.name, roleId: body.roleId, status: body.status };
  return fetchWithRefresh(target, {
    method: isCreate ? "POST" : "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

function toFieldErrors(httpStatus: number, body: ErrorBody): FieldErrors {
  let singleResult: FieldErrors;
  if (httpStatus === 409) {
    singleResult = { login: "Пользователь с таким логином уже существует" };
  } else {
    if (httpStatus === 403) {
      singleResult = { general: "Недостаточно прав для этого действия" };
    } else {
      const fields = body.fields === undefined ? [] : body.fields;
      const fieldErrors: FieldErrors = {};
      if (fields.includes("login")) {
        fieldErrors.login = "Укажите логин";
      }
      if (fields.includes("name")) {
        fieldErrors.name = "Укажите имя";
      }
      if (fields.includes("roleId")) {
        fieldErrors.roleId = "Выберите роль";
      }
      const noFieldNamed =
        fieldErrors.login === undefined && fieldErrors.name === undefined && fieldErrors.roleId === undefined;
      if (noFieldNamed) {
        fieldErrors.general = "Не удалось сохранить пользователя";
      }
      singleResult = fieldErrors;
    }
  }
  return singleResult;
}
